import { useState } from 'react';

import { PaginationState } from '@tanstack/react-table';

import ReconciliationRunDetailsGate from 'app/components/Reconciliations/ReconciliationRunDetailsGate';
import ReconciliationPagedTable from 'app/components/Reconciliations/ReconciliationPagedTable';
import ResolveFindingDialog from 'app/components/Reconciliations/ResolveFindingDialog';

import {
  SalaryDiscrepancyTabKey,
  SalaryDiscrepancyTabViews,
} from 'app/constants/salaryReconciliations';
import { defaultPageSize } from 'app/constants/table';

import useFetchSalaryDiscrepancies from 'app/hooks/useFetchSalaryDiscrepancies';

import getSalaryDiscrepancyColumns from 'app/utils/table/salaryDiscrepancies';

const EMPTY_TEXT: Record<SalaryDiscrepancyTabKey, string> = {
  new: 'This run found no new problems',
  resolved: 'This run resolved no problems',
  past: 'There are no past problems that were never fixed',
  fixed: 'No problems have been fixed by hand',
};

interface SalaryDiscrepanciesTabProps {
  reconciliationId: string;
  mode: SalaryDiscrepancyTabKey;
  companyId?: string;
  isRunning: boolean;
}

/**
 * Lists the salary accrual discrepancies a run found for the first time, the ones it resolved, those from earlier pay
 * cycles that were never fixed, or those an admin fixed by hand. New and past discrepancies still open can be marked
 * as fixed from here.
 */
function SalaryDiscrepanciesTab({
  reconciliationId,
  mode,
  companyId = undefined,
  isRunning,
}: SalaryDiscrepanciesTabProps) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [discrepancyToResolve, setDiscrepancyToResolve] = useState<string | undefined>();
  const isFixed = mode === 'fixed';
  const canResolve = mode === 'new' || mode === 'past';

  const { data, isLoading, isError } = useFetchSalaryDiscrepancies(
    {
      id: reconciliationId,
      view: SalaryDiscrepancyTabViews[mode],
      companyId,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    !isRunning
  );

  return (
    <ReconciliationRunDetailsGate isRunning={isRunning} detailsAvailable pendingText="Its problems">
      <>
        <ReconciliationPagedTable
          page={data}
          isLoading={isLoading}
          isError={isError}
          columns={getSalaryDiscrepancyColumns({
            showCompanyColumn: !companyId,
            showResolvedAt: mode === 'resolved' || isFixed,
            showResolvedBy: isFixed,
            onResolve: canResolve
              ? (discrepancy) => setDiscrepancyToResolve(discrepancy.id)
              : undefined,
          })}
          pagination={pagination}
          onPaginationChange={setPagination}
          getRowId={(discrepancy) => discrepancy.id}
          emptyText={EMPTY_TEXT[mode]}
          errorText="Sorry, we failed to fetch these problems. Please try again."
          testId={`salary-reconciliation-${mode}-discrepancies`}
        />
        <ResolveFindingDialog
          kind="salary"
          findingId={discrepancyToResolve}
          onClose={() => setDiscrepancyToResolve(undefined)}
        />
      </>
    </ReconciliationRunDetailsGate>
  );
}

export default SalaryDiscrepanciesTab;
